import { motion } from "framer-motion";
import { forwardRef, useEffect, useState } from "react";
import { useProductData } from "@/zustand/productStore.js";
import { useCartData } from "@/zustand/cartStore.js";
import { useReducedProductsPrice } from "@/func/productCardHelpers.js";
import LazyLoad from "react-lazyload";
import ProductSelectorButton from "@/components/ProductSelectorButton";
import parse from "html-react-parser";
import { formatTextWithBreak } from "@/utils/helpers";

const ProductPackageItemV2 = forwardRef((props, ref) => {
  const productData = useProductData();
  const base = window.post_template;
  const { discountCode, isSubscribe } = useCartData();
  const { product, handleChange, active, index } = props;
  const [variantImage, setVariantImage] = useState();
  const isBestSeller = product.best_seller;
  const isBestValue = product.best_value;
  const discountSaveLabel = product.discount_save_label;
  const discountOffLabel = product.discount_off_label;
  const productsContent = window.post_template?.checkout_v4_products;
  const outlineBorder = productsContent.checkout_v4_products_imageWrap;
  const showBulletPoints = base?.checkout_v4_summary.checkout_v4_summary_bullet_points ?? false;

  const bestSellerLabel = window?.post_template?.checkout_v4_products?.tr_checkout_v4_products_bestSellerLabel;
  const bestValueLabel = window?.post_template?.checkout_v4_products?.tr_checkout_v4_products_bestValueLabel;

  let bestValueContent = null;
  let bestSellerContent = null;
  if (isBestValue && product?.tr_best_value_status) {
    // old from product pack
    bestValueContent = formatTextWithBreak(product.tr_best_value_status?.toUpperCase());
  } else if (bestValueLabel) {
    // new approach from template
    bestValueContent = formatTextWithBreak(bestValueLabel?.toUpperCase());
  }
  if (isBestSeller && product?.status) {
    // old from product pack
    bestSellerContent = formatTextWithBreak(product.status?.toUpperCase());
  } else if (bestSellerLabel) {
    // new approach from template
    bestSellerContent = formatTextWithBreak(bestSellerLabel?.toUpperCase());
  }

  const productInfo = useReducedProductsPrice(product, productData?.currency_code, productData?.formatted_string);
  const { priceObject, promoPriceObject } = productInfo;

  const productPriceObject =
    productData?.enable_discount_codes && discountCode !== undefined ? promoPriceObject : priceObject;
  const discountPercent =
    productData.enable_discount_codes && discountCode !== undefined
      ? product.promo_discount_amount_percent
      : product.you_save_percentage;

  // Generic defaults
  let pickerTitle = `${product.amount}x ${product.display_name}`;
  let pickerText = product?.tr_bundle_products;
  let pickerImageOverride;

  // Subscription ProductPicker
  const subscription = productData?.subscription;
  const subTemplate = window.subscription_template;
  const subscriptionActive = subscription?.subscription_enable && subTemplate;

  // Reoeater values
  if (subscriptionActive) {
    // Dynamic text
    const durations = subTemplate.receive_duration?.split(",") || [];
    const duration = durations[index];

    const baseDurationLabel = subTemplate.tr_receive_duration_label || "";

    const durationLabel = baseDurationLabel.includes("###react_duration###")
      ? duration
        ? baseDurationLabel.replace("###react_duration###", duration)
        : ""
      : baseDurationLabel;

    const supplyLabel = subTemplate.tr_month_supply_label;
    if (supplyLabel) {
      pickerText = `${product.amount}-${supplyLabel}<br>`;
    }

    if (isSubscribe && durationLabel) {
      pickerText += durationLabel;
    }

    // Repeaater Overwrite
    const productPickerArr = (isSubscribe ? subTemplate.subscription_products : subTemplate.onetime_products) || [];

    const matchingPicker = productPickerArr.find((obj) => obj.product_amount == product.amount);

    if (matchingPicker) {
      pickerTitle = matchingPicker.tr_picker_title || pickerTitle;
      pickerText = matchingPicker.tr_picker_text.trim() || pickerText;
      pickerImageOverride = matchingPicker.product_image;
    }
  }

  // Image definition
  const checkoutImages = window.post_template?.checkout_v4_products?.checkout_v4_product_images || [];
  const matchingImage = checkoutImages.find((obj) => obj.product_amount == product.amount)?.product_image;

  const defaultImage = pickerImageOverride || matchingImage || product.image.url;
  const image = variantImage || defaultImage;

  const isEnhancedContrast = window.enhancedContrast;

  return (
    <div
      data-testid="product-container"
      onClick={() => {
        handleChange(index);
      }}
      id={`productCard-${product.amount}`}
      className={`${active ? ["productPackages_v2__container productPackage_v2__container--visible"] : ["productPackages_v2__container productPackages_v2__container--unchecked"]} ${isEnhancedContrast && "enhancedContrast"}`}
    >
      <div className="productPacItem_v2__firstBlock">
        {showBulletPoints !== undefined && showBulletPoints !== null && !showBulletPoints && (
          <ProductSelectorButton useEnhancedContrast={isEnhancedContrast && !active} />
        )}
        <LazyLoad offset={100}>
          <motion.div
            key={image}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              delay: 1,
              default: { duration: 0.5 },
            }}
          >
            <img
              className={`productPacItem_v2__image mon-img-85-w mon-img-85-h ${
                outlineBorder ? "productPacItem_v2__image--border" : ""
              }`}
              src={image}
              alt={product.image.alt}
            />
          </motion.div>
        </LazyLoad>
        <div className="productPacItem_v2__label__container">
          {isBestSeller && <div className="productPacItem_v2__priceBlock__labelTag">{bestSellerContent}</div>}
          {isBestValue && <div className="productPacItem_v2__priceBlock__bestValueTag">{bestValueContent}</div>}
          <span data-testid="product-amount" className="productPacItem_v2__label__amount">
            {pickerTitle}
          </span>
          <div
            data-testid="product-bundle-items"
            className={`productPacItem_v2__label__bundleItems ${isEnhancedContrast && "enhancedContrast"}`}
          >
            {parse(pickerText)}
          </div>
        </div>
      </div>
      <div className="productPacItem_v2__priceBlock__container">
        <div data-testid="product-price-old" className="productPacItem_v2__priceBlock__oldPrice">
          {productInfo.oldPrice}
        </div>
        <div className="productPacItem_v2__priceBlock__mainPriceBlock">
          <div data-testid="product-price" className="productPacItem_v2__priceBlock__price">
            {productPriceObject?.symbolLeft ? (
              <>
                <sup className="productPacItem_v2__priceBlock__symbol">{productPriceObject?.symbol}</sup>
                {productPriceObject?.number}
                {productPriceObject?.hasCents && `.${productPriceObject?.cents}`}
              </>
            ) : (
              <>
                {productPriceObject?.number}
                {productPriceObject?.hasCents && `.${productPriceObject?.cents}`}
                <sup className="productPacItem_v2__priceBlock__symbol">{productPriceObject?.symbol}</sup>
              </>
            )}
          </div>
          <div className="productPacItem_v2__priceBlock__centsBlock">
            <div
              className={`productPacItem_v2__priceBlock__each
                ${
                  productPriceObject?.hasCents
                    ? `productPacItem_v2__priceBlock__each--with-cents`
                    : `productPacItem_v2__priceBlock__each--without-cents`
                } `}
            >
              {`/`}
              {window.post_template.checkout_v4_products.tr_checkout_v4_products_eachLabel}
            </div>
          </div>
        </div>
        <div className="productPacItem_v2__discountPercent">
          <p>
            {discountSaveLabel} {discountPercent}% {discountOffLabel}
          </p>
        </div>
      </div>
    </div>
  );
});

export { ProductPackageItemV2 };
